"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listItemsFactory = exports.defaultListItems = exports.useScrollableSource = void 0;
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const react_intl_1 = require("react-intl");
const snackbar_context_1 = require("../composite/snackbar/snackbar-context");
const snackbar_types_1 = require("../composite/snackbar/snackbar-types");
const abortable_fetch_1 = require("./abortable-fetch");
const error_utils_1 = require("./error-utils");
const event_callback_hook_1 = require("./event-callback-hook");
const serialize_call_hook_1 = require("./serialize-call-hook");
function useScrollableSource({ url = '', params, listItems = defaultListItems, } = {}) {
    var _a;
    const fetch = (0, react_1.useRef)(null);
    const urlRef = (0, react_1.useRef)(url);
    const paramsRef = (0, react_1.useRef)({ size: (_a = params === null || params === void 0 ? void 0 : params.size) !== null && _a !== void 0 ? _a : 30 });
    const dataRef = (0, react_1.useRef)({
        items: [],
        count: 0,
    });
    const isDataValidRef = (0, react_1.useRef)(false);
    const hasNextPageRef = (0, react_1.useRef)(true);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [result, setResult] = (0, react_1.useState)(dataRef.current);
    const intl = (0, react_intl_1.useIntl)();
    const { showSnackbar } = (0, react_1.useContext)(snackbar_context_1.SnackbarContext);
    (0, react_1.useEffect)(() => {
        if (url) {
            urlRef.current = url;
        }
    }, [url]);
    const loadMoreUnsafe = (0, event_callback_hook_1.useEventCallback)(async () => {
        var _a, _b, _c, _d, _e;
        try {
            if (isDataValidRef.current && !hasNextPageRef.current) {
                // skip
                return dataRef.current;
            }
            if (!urlRef.current) {
                console.error('Can not load scrollableSource without valid URL');
            }
            setLoading(true);
            const combinedParams = {
                ...params,
                ...((_a = paramsRef.current) !== null && _a !== void 0 ? _a : {}),
                searchAfter: isDataValidRef.current
                    ? dataRef.current.searchAfter
                    : undefined,
            };
            fetch.current = listItems(urlRef.current, combinedParams);
            const data = await fetch.current.json();
            if (!isDataValidRef.current) {
                dataRef.current = data;
            }
            else {
                dataRef.current = {
                    ...data,
                    items: [...dataRef.current.items, ...data.items],
                    highlights: [
                        ...((_b = dataRef.current.highlights) !== null && _b !== void 0 ? _b : []),
                        ...((_c = data.highlights) !== null && _c !== void 0 ? _c : []),
                    ],
                    matchedQueries: [
                        ...((_d = dataRef.current.matchedQueries) !== null && _d !== void 0 ? _d : []),
                        ...((_e = data.matchedQueries) !== null && _e !== void 0 ? _e : []),
                    ],
                };
            }
            isDataValidRef.current = true;
            hasNextPageRef.current = data.count > dataRef.current.items.length;
            (0, react_dom_1.unstable_batchedUpdates)(() => {
                setResult(dataRef.current);
                setLoading(false);
            });
            fetch.current = null;
        }
        catch (err) {
            if (err.name !== 'AbortError') {
                isDataValidRef.current = true;
                hasNextPageRef.current = false;
                const errorDetail = (0, error_utils_1.getErrorMessage)(err);
                const message = intl.formatMessage({
                    id: 'EAS_SCROLLABLE_SOURCE_MSG_ERROR',
                    defaultMessage: 'Chyba načtení dat: {detail}',
                }, { detail: errorDetail });
                setLoading(false);
                showSnackbar(message, snackbar_types_1.SnackbarVariant.ERROR);
            }
        }
        return dataRef.current;
    });
    const [loadMore] = (0, serialize_call_hook_1.useSerializeCall)(loadMoreUnsafe);
    const setUrl = (0, event_callback_hook_1.useEventCallback)((url) => (urlRef.current = url));
    const setParams = (0, event_callback_hook_1.useEventCallback)((p) => (paramsRef.current = p));
    const getParams = (0, event_callback_hook_1.useEventCallback)(() => paramsRef.current);
    const reset = (0, event_callback_hook_1.useEventCallback)(() => {
        (0, react_dom_1.unstable_batchedUpdates)(() => {
            var _a;
            (_a = fetch.current) === null || _a === void 0 ? void 0 : _a.abort();
            hasNextPageRef.current = true;
            isDataValidRef.current = false;
            setLoading(false);
            setResult({
                items: [],
                highlights: [],
                matchedQueries: [],
                count: 0,
            });
        });
    });
    const hasNextPage = (0, event_callback_hook_1.useEventCallback)(() => hasNextPageRef.current);
    const isDataValid = (0, event_callback_hook_1.useEventCallback)(() => isDataValidRef.current);
    const source = (0, react_1.useMemo)(() => ({
        ...result,
        loading,
        setLoading,
        loadMore,
        setUrl,
        setParams,
        getParams,
        reset,
        hasNextPage,
        isDataValid,
    }), [
        result,
        loading,
        loadMore,
        setUrl,
        setParams,
        getParams,
        reset,
        hasNextPage,
        isDataValid,
    ]);
    return source;
}
exports.useScrollableSource = useScrollableSource;
function defaultListItems(url, params) {
    return (0, abortable_fetch_1.abortableFetch)(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
    });
}
exports.defaultListItems = defaultListItems;
function listItemsFactory({ listItemsMethod = defaultListItems, postProcess = async (data) => data, }) {
    return function listItems(url, params) {
        const fetch = listItemsMethod(url, params);
        const augmented = {
            response: fetch.response,
            abort: fetch.abort,
            json: async () => {
                const data = await fetch.json();
                const processedData = postProcess(data);
                return processedData;
            },
            text: async () => {
                throw new Error('Unsupported operation');
            },
            raw: async () => {
                throw new Error('Unsupported operation');
            },
            none: async () => {
                throw new Error('Unsupported operation');
            },
        };
        return augmented;
    };
}
exports.listItemsFactory = listItemsFactory;
//# sourceMappingURL=scrollable-source-hook.js.map